import React, { useEffect, useRef } from 'react';

interface Crxcr2CamsWidgetProps {
  lang?: string; // e.g. 'en', 'fr'
  mode?: string; // e.g. 'prerecorded'
  outlinkUrl?: string;
  className?: string;
}

/**
 * Lightweight wrapper to inject the CRXCR2 cams widget script
 * Example vendor snippet:
 * <script defer src="https://crxcr2.com/cams-widget-ext/im_jerky?lang=en&mode=prerecorded&outlinkUrl=..." />
 */
const Crxcr2CamsWidget: React.FC<Crxcr2CamsWidgetProps> = ({
  lang = 'en',
  mode = 'prerecorded',
  outlinkUrl = 'https://t.vlmai-3.com/408074/10339/0?aff_sub5=SF_006OG000004lmDN&aff_sub4=AT_0018',
  className,
}) => {
  // CRXCR2 cams widget enabled
  const containerRef = useRef<HTMLDivElement>(null);
  const injectedRef = useRef(false);
  const retriedOnceRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Enable widget on mobile as requested
    const allowMobile = true;

    // Mobile guard: was blocking on mobile; now allowed when allowMobile=true
    try {
      const isMobileViewport = (() => { try { return !!window.matchMedia && window.matchMedia('(max-width: 768px)').matches; } catch { return window.innerWidth <= 768; } })();
      const isMobileUA = (() => { try { return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent); } catch { return false; } })();
      const isMobile = isMobileViewport || isMobileUA;
      if (isMobile && !allowMobile) {
        // Ensure any previously injected vendor artifacts are pruned on mobile
        try {
          document
            .querySelectorAll('#jerky-im-avatar-bubble, .jerky-im-avatar')
            .forEach((el) => (el as HTMLElement).style.setProperty('display', 'none', 'important'));
          document
            .querySelectorAll('#jerky-im-avatar-bubble, .jerky-im-avatar')
            .forEach((el) => el.parentElement?.removeChild(el as Element));
        } catch {}
        try {
          const cur = document.querySelector(
            'script[src^="https://crxcr2.com/cams-widget-ext/im_jerky"]'
          ) as HTMLScriptElement | null;
          cur?.parentElement?.removeChild(cur);
        } catch {}
        return () => {
          try {
            document
              .querySelectorAll('#jerky-im-avatar-bubble, .jerky-im-avatar')
              .forEach((el) => el.parentElement?.removeChild(el as Element));
          } catch {}
          try {
            const cur = document.querySelector(
              'script[src^="https://crxcr2.com/cams-widget-ext/im_jerky"]'
            ) as HTMLScriptElement | null;
            cur?.parentElement?.removeChild(cur);
          } catch {}
        };
      }
    } catch {}

    const removeJerkyDom = () => {
      try {
        document
          .querySelectorAll('#jerky-im-avatar-bubble, .jerky-im-avatar')
          .forEach((el) => el.parentElement?.removeChild(el as Element));
      } catch {}
    };
    const pruneJerkyDuplicates = () => {
      try {
        const containerNodes = Array.from(document.querySelectorAll('#jerky-im-avatar-bubble')) as HTMLElement[];
        if (containerNodes.length > 1) {
          containerNodes.slice(1).forEach((n) => { try { n.parentElement?.removeChild(n); } catch {} });
        }
        const avatarNodes = Array.from(document.querySelectorAll('.jerky-im-avatar')) as HTMLElement[];
        if (avatarNodes.length > 1) {
          avatarNodes.slice(1).forEach((n) => { try { n.parentElement?.removeChild(n); } catch {} });
        }
      } catch {}
    };
    const removeExistingScript = () => {
      try {
        const cur = document.querySelector(
          'script[src^="https://crxcr2.com/cams-widget-ext/im_jerky"]'
        ) as HTMLScriptElement | null;
        cur?.parentElement?.removeChild(cur);
      } catch {}
    };

    const alreadyLoaded = (window as any).__crxcr2CamsWidgetLoaded === true;
    const isLoading = (window as any).__crxcr2CamsWidgetLoading === true;
    const loadedUrl = (window as any).__crxcr2CamsWidgetUrl as string | undefined;
    const loadingAt = (window as any).__crxcr2CamsWidgetLoadingAt as number | undefined;
    const loadingTooLong = !!loadingAt && (Date.now() - loadingAt > 8000);
    const containerCount = document.querySelectorAll('#jerky-im-avatar-bubble').length;
    const avatarCount = document.querySelectorAll('.jerky-im-avatar').length;
    const bubblePresent = containerCount > 0 || avatarCount > 0;
    const duplicatesPresent = containerCount > 1 || avatarCount > 1;

    // Real-time pruning of duplicates (in case vendor adds a second one)
    if (duplicatesPresent) pruneJerkyDuplicates();

    // If a load is already in progress for the same (or not-yet-known) URL, skip reinjection unless it's taking too long
    if ((isLoading && (!loadedUrl || loadedUrl === outlinkUrl)) && !loadingTooLong) {
      return;
    }

    // Skip reinjection when exactly one bubble exists and widget is already loaded for same URL
    if (bubblePresent && alreadyLoaded && loadedUrl === outlinkUrl) {
      return;
    }

    // If URL changed or duplicates/leftovers exist or no bubble exists, cleanup before (re)injecting
    if ((alreadyLoaded && loadedUrl !== outlinkUrl) || duplicatesPresent || !bubblePresent) {
      removeJerkyDom();
      removeExistingScript();
    }

    // Timer to allow a one-time reinjection if no bubble appears after vendor script load
    let postLoadTimer: number | null = null;

    const inject = () => {
      if (injectedRef.current) return;
      injectedRef.current = true;

      const src = `https://crxcr2.com/cams-widget-ext/im_jerky?lang=${lang}&mode=${mode}&outlinkUrl=${outlinkUrl}`;

      const s = document.createElement('script');
      s.src = src;
      s.defer = true;
      s.setAttribute('data-vendor', 'crxcr2-cams-widget');
      try { console.info('[CRXCR2] injecting cams widget:', src); } catch {}
      // Mark loading immediately to prevent StrictMode remount from double-injecting
      try { 
        (window as any).__crxcr2CamsWidgetLoading = true; 
        (window as any).__crxcr2CamsWidgetLoadingAt = Date.now();
      } catch {}
      s.onload = () => {
        try {
          console.info('[CRXCR2] cams widget loaded');
          (window as any).__crxcr2CamsWidgetLoaded = true;
          (window as any).__crxcr2CamsWidgetUrl = outlinkUrl;
          (window as any).__crxcr2CamsWidgetLoading = false;
          (window as any).__crxcr2CamsWidgetLoadingAt = undefined;
        } catch {}
        // After load, prune any duplicate bubbles that might have appeared
        try { pruneJerkyDuplicates(); } catch {}
        // If no bubble is present shortly after load, retry injection once
        try {
          postLoadTimer = window.setTimeout(() => {
            try {
              const hasBubble = !!document.getElementById('jerky-im-avatar-bubble') || !!document.querySelector('.jerky-im-avatar');
              if (!hasBubble && !retriedOnceRef.current) {
                console.warn('[CRXCR2] No bubble detected post-load; retrying once');
                retriedOnceRef.current = true;
                injectedRef.current = false;
                removeExistingScript();
                inject();
              }
            } catch {}
          }, 3000);
        } catch {}
      };
      s.onerror = (e) => { 
        try { 
          console.error('[CRXCR2] cams widget failed to load', e); 
          (window as any).__crxcr2CamsWidgetLoading = false; 
          (window as any).__crxcr2CamsWidgetLoadingAt = undefined;
        } catch {} 
        // Retry once after a short delay
        try {
          setTimeout(() => { try { injectedRef.current = false; removeExistingScript(); inject(); } catch {} }, 2000);
        } catch {}
      };

      // Prefer injecting near our container to ease cleanup
      try {
        (document.head || document.body).appendChild(s);
      } catch {
        (containerRef.current || document.body).appendChild(s);
      }
    };

    // Observe DOM to continuously prune duplicates appearing after vendor script runs
    let mo: MutationObserver | null = null;
    try {
      mo = new MutationObserver(() => pruneJerkyDuplicates());
      mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
    } catch {}

    // Periodic pruning fallback for a short window after mount (vendor may append late)
    let pruneInterval: number | null = null;
    let pruneStopTimer: number | null = null;
    try {
      pruneInterval = window.setInterval(() => { try { pruneJerkyDuplicates(); } catch {} }, 800);
      pruneStopTimer = window.setTimeout(() => { try { if (pruneInterval) window.clearInterval(pruneInterval); } catch {} }, 15000);
    } catch {}

    // Load immediately and also on consent events + timed fallback
    try { inject(); } catch {}
    const onOsanoInit = () => inject();
    const onOsanoChange = () => inject();
    try { document.addEventListener('osano-cm-initialized', onOsanoInit as any); } catch {}
    try { document.addEventListener('osano-cm-consentchanged', onOsanoChange as any); } catch {}
    const t = window.setTimeout(() => { try { inject(); } catch {} }, 1200);

    return () => {
      try { document.removeEventListener('osano-cm-initialized', onOsanoInit as any); } catch {}
      try { document.removeEventListener('osano-cm-consentchanged', onOsanoChange as any); } catch {}
      try { window.clearTimeout(t); } catch {}
      try { if (postLoadTimer) window.clearTimeout(postLoadTimer); } catch {}
      try { mo?.disconnect(); } catch {}
      try { if (pruneInterval) window.clearInterval(pruneInterval); } catch {}
      try { if (pruneStopTimer) window.clearTimeout(pruneStopTimer); } catch {}
      // Do not remove the bubble on normal unmount; only scripts and container cleanup
      try { removeExistingScript(); } catch {}
      try { if (containerRef.current) containerRef.current.innerHTML = ''; } catch {}
      injectedRef.current = false;
    };
  }, [lang, mode, outlinkUrl]);

  return <div ref={containerRef} className={className} aria-label="Cams widget" />;
};

export default Crxcr2CamsWidget;
